import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { DayAvailability, TimeSlot } from '../../api';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { CalendarWithAvailability } from '@features/visits/components/visit-form/components/CalendarWithAvailability';

type TimeFilter = 'all' | 'morning' | 'afternoon';

const isMorning = (slot: TimeSlot) => {
  const hour = parseInt(slot.start.split(':')[0], 10);
  return hour < 12;
};

const isAfternoon = (slot: TimeSlot) => {
  const hour = parseInt(slot.start.split(':')[0], 10);
  return hour >= 12;
};

const PRIMARY = '#D02964';

type Props = {
  currentMonth: Dayjs;
  selectedDate: Dayjs | null;
  selectedSlot: TimeSlot | null;
  slots: TimeSlot[];
  slotsReason?: string;
  loadingSlots: boolean;
  loadingHighlightedDays: boolean;
  highlightedDays: Record<string, DayAvailability>;
  onDateChange: (d: Dayjs | null) => void;
  onMonthChange: (m: Dayjs) => void;
  onSlotClick: (s: TimeSlot) => void;
};

const StepDateTime = ({
  currentMonth,
  selectedDate,
  selectedSlot,
  slots,
  slotsReason,
  loadingSlots,
  loadingHighlightedDays,
  highlightedDays,
  onDateChange,
  onMonthChange,
  onSlotClick,
}: Props) => {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const { t } = useIntlTranslations('stepDateTime');

  const filteredSlots = slots.filter(slot => {
    if (timeFilter === 'morning') {
      return isMorning(slot);
    }
    if (timeFilter === 'afternoon') {
      return isAfternoon(slot);
    }
    return true;
  });

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700} color="text.primary">
        {t('selectDate')}
      </Typography>
      <Alert severity="info" sx={{ borderRadius: '12px' }}>
        {t('bookingNotice')}
      </Alert>
      <Box
        sx={{
          borderRadius: '14px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}
      >
        <CalendarWithAvailability
          value={selectedDate}
          referenceDate={currentMonth}
          isLoading={loadingHighlightedDays}
          highlightedDays={highlightedDays}
          onChange={onDateChange}
          onMonthChange={onMonthChange}
          minDate={dayjs().add(48, 'hour').startOf('day')}
        />
      </Box>

      {selectedDate && (
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="text.primary">
              {t('availableTimes', { date: selectedDate.format('D. M. YYYY') })}
            </Typography>
            {slots.length > 0 && (
              <ToggleButtonGroup
                value={timeFilter}
                exclusive
                onChange={(_, v) => v && setTimeFilter(v)}
                size="small"
                sx={{ height: 28 }}
              >
                <ToggleButton value="all" sx={{ px: 1, fontSize: '0.7rem', textTransform: 'none' }}>
                  {t('filterAll')}
                </ToggleButton>
                <ToggleButton
                  value="morning"
                  sx={{ px: 1, fontSize: '0.7rem', textTransform: 'none' }}
                >
                  {t('filterMorning')}
                </ToggleButton>
                <ToggleButton
                  value="afternoon"
                  sx={{ px: 1, fontSize: '0.7rem', textTransform: 'none' }}
                >
                  {t('filterAfternoon')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
          {loadingSlots ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} sx={{ color: PRIMARY }} />
            </Box>
          ) : slots.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              {slotsReason === 'MIN_NOTICE'
                ? t('bookingNotice')
                : slotsReason === 'NOT_WORKING'
                  ? t('notWorking')
                  : t('noSlots')}
            </Alert>
          ) : filteredSlots.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              {t('noSlotsInFilter')}
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                gap: 1,
              }}
            >
              {filteredSlots.map(slot => {
                const isSelected = selectedSlot?.start === slot.start;
                return (
                  <Box
                    key={slot.start}
                    onClick={() => onSlotClick(slot)}
                    sx={{
                      py: 1,
                      px: 0.5,
                      borderRadius: '10px',
                      border: '2px solid',
                      borderColor: isSelected ? PRIMARY : 'divider',
                      bgcolor: isSelected ? PRIMARY : '#fff',
                      color: isSelected ? '#fff' : 'text.primary',
                      textAlign: 'center',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: '0.82rem',
                      transition: 'all 0.15s',
                      boxShadow: isSelected ? `0 2px 8px ${PRIMARY}40` : 'none',
                    }}
                  >
                    {slot.start}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default StepDateTime;
